import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import ApprovalStatusChip from 'components/chip/ApprovalStatusChip';
import TaskStatusChip from 'components/chip/TaskStatusChip';
import TicketCategoryChip from 'components/chip/TicketCategoryChip';
import TicketPriorityChip from 'components/chip/TicketPriorityChip';
import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleSearchFilter,
  ConsoleTable,
  useQueryInitialFilters,
} from 'components/console';
import { MasterTypeResult, getMasterType } from 'stores/MasterTypeStoreAdapter';
import {
  ApprovalStatus,
  TaskStatus,
  TicketListResult,
  listTicket,
} from 'stores/TicketStoreAdapter';
import { TicketTypeSummaryResult, listTicketType } from 'stores/TicketTypeStoreAdapter';
import { formatDate, formatDateOrDash, isSentinelDate } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

import TicketCreateDialog from './components/TicketCreateDialog';
import TicketCreateSplitButton from './components/TicketCreateSplitButton';
import TicketDetailView, { useTicketDetail } from './components/TicketDetailView';
import { closeTicketTab, openTicketInNewTab } from './ticketNavigation';

/** 一覧の表示上限（バックエンドの ticket.limit-size と合わせる想定） */
const LIST_LIMIT = 200;

const TicketList: React.FC = () => {
  const navigate = useNavigate();
  const { userSession, users } = useAuth();
  const { ticketId: urlTicketId } = useParams<{ ticketId?: string }>();

  const [tickets, setTickets] = useState<TicketListResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [progress, setProgress] = useState(true);
  const [types, setTypes] = useState<TicketTypeSummaryResult[]>([]);
  const [priorities, setPriorities] = useState<MasterTypeResult[]>([]);
  const [parents, setParents] = useState<TicketListResult[]>([]);
  const [activeParams, setActiveParams] = useState<Record<string, unknown>>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [createTypeId, setCreateTypeId] = useState<string | undefined>(undefined);
  const activeTypes = useMemo(() => types.filter((t) => t.isActive), [types]);

  const openCreate = (typeId: string) => {
    setCreateTypeId(typeId);
    setCreateOpen(true);
  };

  // ドロワー詳細 / direct-link 詳細
  const drawer = useTicketDetail();
  const direct = useTicketDetail();

  // マスタ取得
  useEffect(() => {
    listTicketType({})
      .then(setTypes)
      .catch(() => setTypes([]));
    getMasterType('priority')
      .then(setPriorities)
      .catch(() => setPriorities([]));
    // 親チケット候補（親のみ）を取得して親チケット検索の選択肢にする
    listTicket({ onlyParent: true })
      .then((res) => setParents(res.results))
      .catch(() => setParents([]));
  }, []);

  // direct-link 詳細取得
  useEffect(() => {
    if (urlTicketId) direct.load(urlTicketId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTicketId]);

  const priorityName = (code: string | null) =>
    code ? priorities.find((p) => p.typeCode === code)?.typeLabel : undefined;

  const searchConditions: Condition[] = useMemo(
    () => [
      { key: 'managementId', name: '管理ID', type: 'text' as const, color: '#78C1F3' },
      { key: 'keyword', name: 'キーワード', type: 'text' as const, color: '#B0BEC5' },
      {
        key: 'ticketCategory',
        name: '分類',
        type: 'select' as const,
        color: '#B799FF',
        values: [
          { key: 'TASK', name: 'タスク' },
          { key: 'APPROVAL', name: '承認' },
        ],
      },
      {
        key: 'scope',
        name: 'スコープ',
        type: 'select' as const,
        color: '#80CBC4',
        values: [
          { key: 'GLOBAL', name: 'グローバル' },
          { key: 'PRIVATE', name: 'プライベート' },
        ],
      },
      {
        // 同じキーで複数チップを追加すると IN 検索になる（fetchTickets で配列化）
        key: 'typeIds',
        name: '種別',
        type: 'select' as const,
        color: '#B39DDB',
        values: types.map((t) => ({ key: t.typeId, name: t.name })),
      },
      {
        key: 'statuses',
        name: 'ステータス',
        type: 'select' as const,
        color: '#9DC08B',
        values: [
          { key: 'NOT_STARTED', name: '未着手' },
          { key: 'IN_PROGRESS', name: '着手中/対応中' },
          { key: 'DONE', name: '完了' },
          { key: 'CLOSED', name: 'クローズ' },
          { key: 'ADJUSTING', name: '調整中' },
          { key: 'SUBMITTED', name: '申請中' },
          { key: 'APPROVED', name: '承認' },
          { key: 'REJECTED', name: '否決' },
          { key: 'CANCELLED', name: '取消' },
        ],
      },
      {
        key: 'priorities',
        name: '優先度',
        type: 'select' as const,
        color: '#FFCC80',
        values: priorities.map((p) => ({ key: p.typeCode, name: p.typeLabel })),
      },
      {
        key: 'assigneeUserId',
        name: '担当者',
        type: 'user-select-single' as const,
        color: '#F7C8E0',
        users,
      },
      {
        key: 'parentTicketId',
        name: '親チケット',
        type: 'ticket-select-single' as const,
        color: '#A1887F',
        tickets: parents,
      },
      {
        key: 'creatorUserId',
        name: '作成者',
        type: 'user-select-single' as const,
        color: '#FFCC80',
        users,
      },
      {
        key: 'approverUserId',
        name: '承認者',
        type: 'user-select-single' as const,
        color: '#BCAAA4',
        users,
      },
      {
        key: 'watchedByMe',
        name: 'ウォッチ中',
        type: 'check' as const,
        color: '#90CAF9',
        value: { key: 'true', name: 'ウォッチ中' },
      },
      {
        key: 'onlyParent',
        name: '親のみ',
        type: 'check' as const,
        color: '#CE93D8',
        value: { key: 'true', name: '親のみ' },
      },
      {
        key: 'overdueOnly',
        name: '期日超過',
        type: 'check' as const,
        color: '#EF9A9A',
        value: { key: 'true', name: '期日超過' },
      },
      {
        key: 'scheduledStartFrom',
        name: '予定開始(From)',
        type: 'date-time-picker-start' as const,
        color: '#4DD0E1',
      },
      {
        key: 'scheduledStartTo',
        name: '予定開始(To)',
        type: 'date-time-picker-end' as const,
        color: '#4DD0E1',
      },
      {
        key: 'scheduledEndFrom',
        name: '予定終了(From)',
        type: 'date-time-picker-start' as const,
        color: '#81D4FA',
      },
      {
        key: 'scheduledEndTo',
        name: '予定終了(To)',
        type: 'date-time-picker-end' as const,
        color: '#81D4FA',
      },
      {
        key: 'updatedFrom',
        name: '更新日(From)',
        type: 'date-time-picker-start' as const,
        color: '#C5E1A5',
      },
      {
        key: 'updatedTo',
        name: '更新日(To)',
        type: 'date-time-picker-end' as const,
        color: '#C5E1A5',
      },
    ],
    [users, types, priorities, parents],
  );

  // URLクエリ初期条件（ダッシュボードからの遷移用。self 解決）
  const { queryParams, initialFilters } = useQueryInitialFilters(searchConditions, {
    selfUserId: userSession.userId,
    selfKeys: ['assigneeUserId', 'creatorUserId', 'approverUserId'],
  });

  const fetchTickets = (params: Record<string, unknown>) => {
    setProgress(true);
    const payload: Record<string, unknown> = { ...params };
    // IN 検索キーは配列に正規化（同じキーで複数チップ→配列 / 単一→1件配列）
    ['statuses', 'priorities', 'typeIds', 'assigneeUserIds'].forEach((k) => {
      if (payload[k] === undefined || payload[k] === null || payload[k] === '') return;
      if (!Array.isArray(payload[k])) payload[k] = [payload[k]];
    });
    // check 系は boolean 化（false は送らない）
    ['watchedByMe', 'onlyParent', 'overdueOnly'].forEach((k) => {
      if (payload[k] === 'true' || payload[k] === true) payload[k] = true;
      else delete payload[k];
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listTicket(payload as any)
      .then((res) => {
        setTickets(res.results);
        setTotalCount(res.totalCount);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('チケットの取得に失敗しました');
      });
  };

  const refetch = () => fetchTickets(activeParams);

  // 初期・クエリ変更時に取得（direct-link 時は行わない）
  useEffect(() => {
    if (urlTicketId) return;
    setActiveParams(queryParams);
    fetchTickets(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, urlTicketId]);

  // IN 検索対象キー（同じキーで複数チップ→配列に集約）
  const MULTI_KEYS = ['statuses', 'priorities', 'typeIds', 'assigneeUserIds'];

  // クイックフィルタ（よく使う条件をワンタップで適用・業務レビュー）
  const [quickKey, setQuickKey] = useState<string | null>(null);
  const applyQuick = (key: string, params: Record<string, unknown>) => {
    drawer.clear();
    if (quickKey === key) {
      // 同じチップを再タップで解除
      setQuickKey(null);
      setActiveParams({});
      fetchTickets({});
      return;
    }
    setQuickKey(key);
    setActiveParams(params);
    fetchTickets(params);
  };

  const handleSearch = (filters: Filter[]) => {
    setQuickKey(null);
    drawer.clear();
    const params: Record<string, unknown> = {};
    filters.forEach((f) => {
      if (MULTI_KEYS.includes(f.key)) {
        const arr = (params[f.key] as string[] | undefined) ?? [];
        arr.push(f.value.key);
        params[f.key] = arr;
      } else {
        params[f.key] = f.value.key;
      }
    });
    setActiveParams(params);
    fetchTickets(params);
  };

  const statusChip = (row: TicketListResult) =>
    row.ticketCategory === 'TASK' ? (
      <TaskStatusChip status={row.status as TaskStatus} />
    ) : (
      <ApprovalStatusChip status={row.status as ApprovalStatus} />
    );

  const columns: ConsoleColumn<TicketListResult>[] = [
    {
      key: 'managementId',
      header: '管理ID',
      width: 120,
      render: (r) => {
        const isChild = !!r.parentTicketId;
        return (
          <Box
            component="span"
            sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {/* 子/親(単独)を常に固定幅アイコンで表示し、ありなしのズレを防ぐ */}
            <Box
              component="span"
              sx={{ width: 16, flexShrink: 0, display: 'inline-flex', justifyContent: 'center' }}
            >
              {isChild ? (
                <Tooltip title="子チケット">
                  <SubdirectoryArrowRightIcon sx={{ fontSize: 15, color: 'info.main' }} />
                </Tooltip>
              ) : (
                <Tooltip title="親・単独チケット">
                  <ConfirmationNumberOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </Tooltip>
              )}
            </Box>
            {r.managementId}
          </Box>
        );
      },
    },
    { key: 'typeName', header: '種別', width: 120, ellipsis: (r) => r.typeName },
    {
      key: 'ticketCategory',
      header: '分類',
      width: 72,
      render: (r) => <TicketCategoryChip ticketCategory={r.ticketCategory} />,
    },
    { key: 'title', header: 'タイトル', width: 240, ellipsis: (r) => r.title },
    { key: 'status', header: 'ステータス', width: 96, render: statusChip },
    {
      key: 'priority',
      header: '優先度',
      width: 72,
      render: (r) =>
        r.ticketCategory === 'TASK' && r.priority ? (
          <TicketPriorityChip code={r.priority} label={priorityName(r.priority)} />
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>
            —
          </Box>
        ),
    },
    { key: 'assigneeUserName', header: '担当者', width: 110, ellipsis: (r) => r.assigneeUserName },
    {
      key: 'scheduledEndDate',
      header: '終了予定日',
      width: 100,
      render: (r) => {
        if (r.ticketCategory !== 'TASK') {
          return (
            <Box component="span" sx={{ color: 'text.disabled' }}>
              —
            </Box>
          );
        }
        // 期日超過（予定終了日 < 現在・未完了/未クローズ）は赤字で強調（業務レビュー）
        const overdue =
          !isSentinelDate(r.scheduledEndDate) &&
          r.scheduledEndDate !== null &&
          new Date(r.scheduledEndDate) < new Date() &&
          r.status !== 'DONE' &&
          r.status !== 'CLOSED';
        return (
          <Box
            component="span"
            sx={{
              color: overdue ? 'error.main' : 'text.secondary',
              fontWeight: overdue ? 700 : 400,
            }}
          >
            {formatDateOrDash(r.scheduledEndDate)}
          </Box>
        );
      },
    },
    {
      key: 'updatedAt',
      header: '更新日',
      width: 100,
      render: (r) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {formatDate(r.updatedAt)}
        </Box>
      ),
    },
  ];

  // direct-link（/ticket/:ticketId）: フル詳細表示
  if (urlTicketId) {
    if (!direct.detail) {
      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          <Typography variant="body2" color="text.secondary">
            {direct.loading ? '' : 'チケットが見つかりませんでした'}
          </Typography>
          <Circular open={direct.loading} />
        </Box>
      );
    }
    return (
      <TicketDetailView
        detail={direct.detail}
        priorities={priorities}
        onReload={() => direct.load(urlTicketId)}
        onOpenTicket={openTicketInNewTab}
        onBack={closeTicketTab}
      />
    );
  }

  const overLimit = totalCount > LIST_LIMIT;

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <ConsoleHeader
        title="チケット一覧"
        count={totalCount}
        description="チケットの検索・確認・操作を行います"
        backLabel="チケット管理"
        onBack={() => navigate('/ticket')}
        onRefresh={refetch}
        actions={<TicketCreateSplitButton activeTypes={activeTypes} onCreate={openCreate} />}
      />

      {/* クイックフィルタ */}
      <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        {[
          {
            key: 'mine',
            label: '自分の担当（未完了）',
            params: {
              assigneeUserId: userSession.userId,
              ticketCategory: 'TASK',
              statuses: ['NOT_STARTED', 'IN_PROGRESS'],
            },
          },
          {
            key: 'approval',
            label: '承認待ち',
            params: { approverUserId: userSession.userId, statuses: ['SUBMITTED', 'IN_PROGRESS'] },
          },
          { key: 'overdue', label: '期日超過', params: { overdueOnly: true } },
          { key: 'watched', label: 'ウォッチ中', params: { watchedByMe: true } },
        ].map((q) => (
          <Chip
            key={q.key}
            label={q.label}
            size="small"
            variant={quickKey === q.key ? 'filled' : 'outlined'}
            color={quickKey === q.key ? 'primary' : undefined}
            onClick={() => applyQuick(q.key, q.params)}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          />
        ))}
      </Stack>

      {overLimit && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
          他 {totalCount - tickets.length} 件は表示されていません。検索条件で絞り込んでください。
        </Typography>
      )}

      <ConsoleTable
        columns={columns}
        rows={tickets}
        rowKey={(r) => r.ticketId}
        selectedKey={drawer.detail?.ticketId}
        onRowClick={(r) => drawer.load(r.ticketId)}
        emptyMessage="チケットがありません。検索条件を変更してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={searchConditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="ticket-list"
            userProfileKey="ticket-list-search"
          />
        }
      />

      <ConsoleDetailDrawer
        open={drawer.detail !== null}
        onClose={drawer.clear}
        title={drawer.detail?.managementId ?? ''}
        badges={
          drawer.detail && (
            <>
              <TicketCategoryChip ticketCategory={drawer.detail.ticketCategory} />
              {drawer.detail.ticketCategory === 'TASK' ? (
                <TaskStatusChip status={drawer.detail.status as TaskStatus} />
              ) : (
                <ApprovalStatusChip status={drawer.detail.status as ApprovalStatus} />
              )}
            </>
          )
        }
        width="min(80vw, 1100px)"
      >
        {drawer.detail && (
          <Box sx={{ height: '100%', mx: -2.5, mb: -2.5 }}>
            <TicketDetailView
              detail={drawer.detail}
              priorities={priorities}
              onReload={() => {
                if (drawer.detail) drawer.load(drawer.detail.ticketId);
                refetch();
              }}
              onOpenTicket={openTicketInNewTab}
            />
          </Box>
        )}
      </ConsoleDetailDrawer>

      <TicketCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refetch}
        activeTypes={activeTypes}
        priorities={priorities}
        initialTypeId={createTypeId}
      />

      <Circular open={progress} />
    </Box>
  );
};

export default TicketList;
